import * as React from 'react';
import { TextEdit } from '../../../text-edit';
import { AffectedFamilyPageProps } from './affected-family-page.types';
import { useAffectedFamilyPageController } from './affected-family-page.controller';
import { TextField } from '../../../text-field';
import { I18n } from 'react-redux-i18n';
import { Fields } from '../../../fields';
import { Flex } from '@common/components/Flex';
import { Title } from '../../../title';
import { Button } from '@common/components/Button';
import { BackBtn } from '../../../back-btn';
import { FieldSelector } from '@common/components/FieldSelector';
import { Field } from '../field';
import { FieldProperty } from '../field/field.types';
import { Loading } from '@common/components/loading';
import { FieldSelectorGetFieldParams } from '@common/components/FieldSelector/field-selector.types';
import { NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES } from '@modules/fhq-design-tool/constants/page';
import { Subtitle } from '../../../subtitle';

import './affected-family-page.styles.scss';

export const AffectedFamilyPage: React.FC<AffectedFamilyPageProps> = (props) => {
  const { cachedData, showConfirmMessage, onGoBack, getFields, getFolders, scrollParentToTop, errors } = props;

  const {
    canChangeFieldOrder,
    canDeleteField,
    isFirstNameField, 
    processedFields,
    loadingPage,
    handleFieldChange,
    goBackFromFieldEdit,
    editingField,
    handleFieldAdd,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    handleFieldsOrderChange,
    handleFieldDelete,
    navigateOnFieldEdit,
    canShowHideField,
    handlePageNameChange,
    handlePageDescriptionChange,
    fieldHasError,
  } = useAffectedFamilyPageController(props);

  if (editingField) {
    return (
      <Field
        errors={errors?.fields ? errors?.fields[editingField.progenyId] : undefined}
        editableProperties={isFirstNameField(editingField) ? [FieldProperty.name, FieldProperty.required] : undefined}
        scrollParentToTop={scrollParentToTop}
        getFieldsForSkipLogic={getFields}
        getFoldersForSkipLogic={getFolders}
        data={editingField}
        onGoBack={goBackFromFieldEdit}
        onChange={handleFieldChange}
        showConfirmMessage={showConfirmMessage}
      />
    );
  }

  if (loadingPage || !cachedData) {
    return (
      <div className="FhqDesignToolQuestionnaireDesignerPage">
        <Loading size={20} margin="20px" />
      </div>
    );
  }

  return (
    <div className="FhqDesignToolQuestionnaireDesignerAffectedPage">
      <BackBtn className="FhqDesignToolQuestionnaireDesignerAffectedPage__back-btn" onClick={onGoBack} />
      <TextEdit 
        errors={errors?.properties?.title}
        required
        metadata={{
          description: 'page-name',
        }}
        value={cachedData.title}
        onChange={handlePageNameChange}
      />
      <Subtitle type="chip" text={I18n.t('Affected family members')} />
      <TextField 
        metadata={{
          description: 'page-description',
        }}
        type="textarea"
        classes={{
          label: 'FhqDesignToolQuestionnaireDesignerAffectedPage__description-label',
          labelInputWrapper: 'FhqDesignToolQuestionnaireDesignerAffectedPage__description-input-label-wrapper'
        }}
        label={I18n.t('Page Description')}
        value={cachedData.description ?? ''}
        onChange={handlePageDescriptionChange}
      />
      <Flex alignItems="center" className="FhqDesignToolQuestionnaireDesignerAffectedPage__add-field-header">
        <Title text={I18n.t('Fields')} margin={0} />
        <Button bsStyle="primary" className="FhqDesignToolQuestionnaireDesignerAffectedPage__add-field-btn" onClick={openFieldChooser}>
          +
        </Button>
      </Flex>
      <Fields 
        hasError={fieldHasError}
        data={processedFields}
        canChangeFieldOrder={canChangeFieldOrder}
        canDeleteField={canDeleteField}
        canShowHideField={canShowHideField}
        onOrderChange={handleFieldsOrderChange}
        showDeleteConfirmMessage={showConfirmMessage}
        onEdit={navigateOnFieldEdit}
        onDelete={handleFieldDelete}
      />
      {fieldChooserOpened && (
        <FieldSelector
          withSearch
          getFolders={getFolders}
          getFields={({ searchKeyword, ...restParams }: FieldSelectorGetFieldParams) => 
            getFields({ searchQuery: searchKeyword, ...restParams, includeTables: true })
              .then((fields) => 
                fields.filter((x) => !NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES.includes(x.fieldTypeId))
              )
          }
          onChange={handleFieldAdd}
          close={closeFieldChooser}
          closeOnChange={false}
          multiple
        />
      )}
    </div>
  );
};